import { Category } from '@mui/icons-material';
import { Box } from '@mui/system';
import { SlColumn, useStacklineTheme, hexToRgba } from '@stackline/ui';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SlSkeleton from 'src/components/BeaconV2Components/SlSkeleton/SlSkeleton';
import { SlRow } from 'src/components/BeaconV2Components/Generic/SlRow';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  getColorForMetricChangeValue,
  getIconForMetricChange
} from 'src/components/BeaconV2Components/utils/chartStyles';
import {
  BEACON_PRO_ENTITY_CARD_HEIGHT,
  BEACON_PRO_ENTITY_CARD_WIDTH,
  ENTITY_CARD_PERIODIC_TABLE_SIZE
} from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { AllCategoriesIcon, DownChevron } from 'src/components/SvgIcons/SvgIcons';
import { useAppSelector, useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { useCategoryEntity } from 'src/utils/Hooks/reduxSelectors';

interface SubCategory {
  id: number;
  subCategoryName: string;
  name: string;
  type: string;
  displayName: string;
}
export interface Category {
  id: number;
  categoryId: number;
  categoryName: string;
  name: string;
  displayName: string;
  childEntities: SubCategory[];
  value: number;
  categoryType: string;
  currentRetailSales: number;
  retailSalesChange: number;
}

interface CategoryCardProps {
  val: Category;
  isLoading: boolean;
  boxBackgroundColor?: string | null;
  boxTextProps?: React.CSSProperties;
}

const SubcategoryList = ({
  childEntities,
  searchParams,
  additionalParams
}: Pick<Category, 'childEntities'> & {
  searchParams: string;
  additionalParams: string;
}) => {
  const max_to_show = 10;
  const MAX_HEIGHT = 265;
  const [showAll, setShowAll] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollThumbRef = useRef<HTMLDivElement>(null);
  const [thumbHeight, setThumbHeight] = useState(0);
  const [thumbTop, setThumbTop] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [needsScroll, setNeedsScroll] = useState(false);

  const hasMoreItems = childEntities?.length > max_to_show;
  // Show scrollbar when:
  // 1. User clicked "View all" (showAll = true), OR
  // 2. Content overflows (including when "View more" button is pushed out of view)
  const shouldEnableScroll = showAll || needsScroll;

  const checkIfNeedsScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    // Temporarily set overflow to hidden to get accurate scrollHeight
    const originalOverflow = scrollContainer.style.overflowY;
    scrollContainer.style.overflowY = 'hidden';

    const { scrollHeight } = scrollContainer;
    const contentNeedsScroll = scrollHeight > MAX_HEIGHT;

    // Restore original overflow
    scrollContainer.style.overflowY = originalOverflow;

    setNeedsScroll(contentNeedsScroll);
  }, []);

  const updateScrollbar = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !shouldEnableScroll) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const scrollRatio = clientHeight / scrollHeight;
    const newThumbHeight = Math.max(40, clientHeight * scrollRatio);
    const maxScrollTop = scrollHeight - clientHeight;
    const newThumbTop = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * (clientHeight - newThumbHeight) : 0;

    setThumbHeight(newThumbHeight);
    setThumbTop(newThumbTop);
  }, [shouldEnableScroll]);

  useEffect(() => {
    checkIfNeedsScroll();
    updateScrollbar();
  }, [showAll, childEntities, checkIfNeedsScroll, updateScrollbar]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleUpdate = () => {
      checkIfNeedsScroll();
      updateScrollbar();
    };

    scrollContainer.addEventListener('scroll', handleUpdate);
    window.addEventListener('resize', handleUpdate);

    return () => {
      scrollContainer.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
    };
  }, [checkIfNeedsScroll, updateScrollbar]);

  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const startY = e.clientY;
    const startTop = thumbTop;
    const { scrollHeight, clientHeight } = scrollContainer;
    const maxScrollTop = scrollHeight - clientHeight;
    const maxThumbTop = clientHeight - thumbHeight;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY;
      const newThumbTop = Math.max(0, Math.min(maxThumbTop, startTop + deltaY));
      const scrollRatio = newThumbTop / maxThumbTop;
      scrollContainer.scrollTop = scrollRatio * maxScrollTop;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleTrackClick = (e: React.MouseEvent) => {
    const scrollContainer = scrollContainerRef.current;
    const scrollThumb = scrollThumbRef.current;
    if (!scrollContainer || !scrollThumb) return;

    const trackRect = e.currentTarget.getBoundingClientRect();
    const clickY = e.clientY - trackRect.top;
    const { scrollHeight, clientHeight } = scrollContainer;
    const maxScrollTop = scrollHeight - clientHeight;
    const maxThumbTop = clientHeight - thumbHeight;

    const targetThumbTop = clickY - thumbHeight / 2;
    const boundedThumbTop = Math.max(0, Math.min(maxThumbTop, targetThumbTop));
    const scrollRatio = boundedThumbTop / maxThumbTop;
    scrollContainer.scrollTop = scrollRatio * maxScrollTop;
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <Box
        ref={scrollContainerRef}
        sx={{
          maxHeight: '260px',
          width: '100%',
          p: 0,
          m: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          overflowY: shouldEnableScroll ? 'scroll' : 'visible',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          pr: shouldEnableScroll ? '10px' : 0
        }}
      >
        {childEntities?.slice(0, showAll ? undefined : max_to_show).map((child) => (
          <Link
            to={`/${child.type}/${child.id}${searchParams}${additionalParams}`}
            key={`link:${child.type}/${child.id}`}
            onClick={(event) => event.stopPropagation()}
          >
            <Text
              variant="body2"
              title={child.name}
              sx={{
                lineHeight: '20px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {child.name}
            </Text>
          </Link>
        ))}
        {hasMoreItems ? (
          <Box
            key={`link:${showAll ? 'view-less' : 'view-more'}}`}
            display="flex"
            flexDirection="row"
            alignItems="center"
            sx={{
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
            onClick={() => setShowAll(!showAll)}
          >
            <Text variant="body2" sx={{ fontWeight: 500, lineHeight: '20px' }}>
              {showAll ? 'View less' : 'View more'}
            </Text>
            <div
              style={{
                width: '16px',
                height: '16px',
                transform: showAll ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            >
              <DownChevron width="16px" height="16px" />
            </div>
          </Box>
        ) : null}
      </Box>

      {shouldEnableScroll && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: '6px',
            height: '260px',
            backgroundColor: '#F6F7F8',
            borderRadius: '3px',
            cursor: 'pointer'
          }}
          onClick={handleTrackClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Box
            ref={scrollThumbRef}
            sx={{
              position: 'absolute',
              width: '6px',
              height: `${thumbHeight}px`,
              top: `${thumbTop}px`,
              backgroundColor: isDragging || isHovered ? '#7E8FA8' : '#BEC7D3',
              borderRadius: '3px',
              cursor: 'grab',
              transition: isDragging ? 'none' : 'background-color 0.2s ease',
              '&:active': {
                cursor: 'grabbing'
              }
            }}
            onMouseDown={handleThumbMouseDown}
          />
        </Box>
      )}
    </Box>
  );
};

/**
 * Generic container Category Card
 */
const CategoryCard: React.FC<CategoryCardProps> = ({ val, isLoading, boxBackgroundColor, boxTextProps }) => {
  const theme = useStacklineTheme();
  const { displayName, childEntities, id, currentRetailSales, retailSalesChange } = val;
  const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
  const formatMetric = useMetricFormatter();
  const isAllCategoryCard = val.id === 0;

  return (
    <div
      style={{
        width: BEACON_PRO_ENTITY_CARD_WIDTH,
        minHeight: BEACON_PRO_ENTITY_CARD_HEIGHT,
        borderRadius: theme.spacing.sm,
        border: `1px solid ${theme.colors.primaryGray}`,
        position: 'relative'
      }}
    >
      <Box sx={{ p: '16px', overflow: 'visible' }}>
        <SlRow>
          <div
            style={{
              backgroundColor: boxBackgroundColor || hexToRgba(theme.colors.accentTeal, 0.1),
              width: ENTITY_CARD_PERIODIC_TABLE_SIZE,
              height: ENTITY_CARD_PERIODIC_TABLE_SIZE,
              borderRadius: theme.spacing.xs,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isAllCategoryCard ? (
              <AllCategoriesIcon width={18} height={18} />
            ) : (
              <Text
                variant="subtitle1"
                sx={{ fontSize: '20px', fontWeight: 'bold' }}
                color="accentTeal"
                {...boxTextProps}
              >
                {displayName.substring(0, 2)}
              </Text>
            )}
          </div>
        </SlRow>
        <SlRow verticalInset="sm" style={{ paddingBottom: '8px', marginTop: '8px' }}>
          <Link to={`/category/${id}${searchParams}${additionalParams}`}>
            <Text variant="subtitle1" sx={{ fontSize: '18px', lineHeight: '24px' }}>
              {displayName}
            </Text>
          </Link>
        </SlRow>
        <SubcategoryList
          childEntities={childEntities}
          searchParams={searchParams}
          additionalParams={additionalParams}
        />
      </Box>
      <Flex paddingX="16px">
        <div style={{ position: 'absolute', bottom: theme.spacing.md }}>
          <SlColumn verticalPosition="center" spacing="xs">
            <Link to={`/category/${id}${searchParams}&tab=sales&subtab=retailSales`}>
              <Text transform="uppercase" variant="subtitle2">
                Retail Sales
              </Text>
            </Link>
            {isLoading ? (
              <SlSkeleton style={{ marginLeft: '-2px' }} variant="rounded" height={20} width={80} />
            ) : (
              <SlRow verticalPosition="center">
                <Text variant="body1">
                  {formatMetric(currentRetailSales, METRICTYPE.MONEY, {
                    decimalPlaces: 1,
                    showFullValue: false
                  })}
                </Text>
                <SlRow horizontalInset="sm" verticalPosition="center">
                  {getIconForMetricChange(retailSalesChange, 2)}
                  <SlRow horizontalInset="xs">
                    <Text variant="subtitle2" color={getColorForMetricChangeValue(retailSalesChange)}>
                      {' '}
                      {formatMetric(Math.abs(retailSalesChange), METRICTYPE.PERCENT, {
                        decimalPlaces: 2,
                        showFullValue: true,
                        showNegative: true
                      })}
                    </Text>
                  </SlRow>
                </SlRow>
              </SlRow>
            )}
          </SlColumn>
        </div>
      </Flex>
    </div>
  );
};

export default CategoryCard;
